import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import axios, { AxiosResponse } from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { quoteLineType, quoteType, salesforceOpportunityType } from '../types'
import { CRMSearch } from '../helper'
import { coreCompetencies, salesforceForecastCategories, salesforceStages, setAsides } from '../lists'
import dayjs from 'dayjs'
import { useUser } from '../hooks'
import './QuoteDetailsOptionsDialogs.css'
import { CurrencyInput, DatePicker } from '@impres-development/assets/client'
import { useQuoteLines } from 'src/EditQuote/lines/QuoteLinesContext'
import { applyNTECalculation } from 'src/EditQuote/lines/lineItemPricing'
import { QuotePdfPreview, downloadQuotePdf } from 'src/EditQuote/steps/export/QuotePdfDocument'
import { aeFromCrm, isrFromCrm, shipToFromCrm, soldToFromCrm } from 'src/EditQuote/steps/export/quotePdfModel'

export function NewVersionDialog(props: {
  open: boolean
  quoteId: string
  nextVersion: number
  onClose: () => void
  contactId?: string
  fromOrderRejection?: boolean
}) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleNewVersion = () => {
    setLoading(true)
    setError(false)
    axios
      .post(`/quote/${props.quoteId}/version`, { contactId: props.contactId })
      .then(res => {
        props.onClose()
        setOpenSnackbar(true)
        navigate(`/quote/${res.data}`)
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>
          {props.fromOrderRejection ? 'Create New Version to Resubmit?' : 'Create New Version?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {props.fromOrderRejection
              ? 'The order was rejected. Create a new quote version to correct and resubmit the order. The new version will open at Won - Draft Order.'
              : 'You are about to create a new version of this quote.'}
          </Typography>
          <Typography>Version #: {props.nextVersion}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleNewVersion} variant="contained" loading={loading}>
            Create New Version
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="New version created successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to create new version. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function CloneDialog(props: { open: boolean; quoteId: string; contactId?: string; onClose: () => void }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleClone = () => {
    setLoading(true)
    setError(false)
    axios
      .post(`/quote/${props.quoteId}/clone`, { contactId: props.contactId })
      .then(res => {
        props.onClose()
        setOpenSnackbar(true)
        navigate(`/quote/${res.data}`)
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Clone</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to create a clone copy of this quote. Are you sure you would like to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleClone} variant="contained" loading={loading}>
            Yes, Proceed to Clone
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote cloned successfully"
      />
      <Snackbar open={error} onClose={() => setError(false)}>
        <Alert severity="error" variant="filled">
          Failed to clone quote. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function DeleteDialog(props: { open: boolean; quoteId: string; onClose: () => void }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleDelete = () => {
    setLoading(true)
    setError(false)
    axios
      .delete(`/quote/${props.quoteId}`)
      .then(res => {
        props.onClose()
        setOpenSnackbar(true)
        if (res.data) {
          navigate(`/quote/${res.data}`)
        } else {
          navigate(`/quote`, { state: { deleted: true } })
        }
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Delete Quote?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this quote? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" loading={loading}>
            Delete Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote deleted successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to delete quote. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function SyncQuoteDialog(props: {
  open: boolean
  onClose: () => void
  handleSync: () => Promise<AxiosResponse<salesforceOpportunityType>>
}) {
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleSync = () => {
    setLoading(true)
    setError(false)
    props
      .handleSync()
      .then(() => {
        props.onClose()
        setOpenSnackbar(true)
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Sync Quote with Salesforce?</DialogTitle>
        <DialogContent>
          <Typography>You are about to update the Salesforce quote with the latest data.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSync} variant="contained" loading={loading}>
            Sync Salesforce Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote synced successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to sync quote with Salesforce. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function NewOpportunityDialog(props: {
  open: boolean
  quoteId: string
  onClose: () => void
  setCrmOpportunity: (opportunity: salesforceOpportunityType) => void
  setAccount: (account: { id: string; label: string } | null) => void
  setContact: (contact: { id: string; label: string } | null) => void
  setOpportunity: (opportunity: { id: string; label: string } | null) => void
}) {
  const user = useUser()

  const [loading, setLoading] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; label: string } | null>(null)
  const [selectedContact, setSelectedContact] = useState<{ id: string; label: string } | null>(null)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [opportunity, setOpportunity] = useState<Partial<salesforceOpportunityType>>({
    Name: '',
    CloseDate: null,
    StageName: '',
    ForecastCategoryName: '',
    Line_Of_Business__c: [],
    Set_Asides__c: [],
  })
  const [missingFields, setMissingFields] = useState({
    Name: false,
    CloseDate: false,
    StageName: false,
    ForecastCategoryName: false,
    Line_Of_Business__c: false,
    Set_Asides__c: false,
    Account: false,
    Contact: false,
  })
  const [createError, setCreateError] = useState(false)

  const handleClose = () => {
    setOpportunity({
      Name: '',
      CloseDate: null,
      StageName: '',
      ForecastCategoryName: '',
      Line_Of_Business__c: [],
      Set_Asides__c: [],
    })
    setMissingFields({
      Name: false,
      CloseDate: false,
      StageName: false,
      ForecastCategoryName: false,
      Line_Of_Business__c: false,
      Set_Asides__c: false,
      Account: false,
      Contact: false,
    })
    setSelectedAccount(null)
    setSelectedContact(null)
    props.onClose()
  }

  const handleCreateOpportunity = () => {
    const missingFieldsObj = {
      Name: !opportunity.Name || opportunity.Name.trim() === '',
      CloseDate: !opportunity.CloseDate,
      StageName: !opportunity.StageName || opportunity.StageName.trim() === '',
      ForecastCategoryName: !opportunity.ForecastCategoryName || opportunity.ForecastCategoryName.trim() === '',
      Line_Of_Business__c: !opportunity.Line_Of_Business__c || opportunity.Line_Of_Business__c.length === 0,
      Set_Asides__c: !opportunity.Set_Asides__c || opportunity.Set_Asides__c.length === 0,
      Account: !selectedAccount || selectedAccount.id.trim() === '',
      Contact: !selectedContact || selectedContact.id.trim() === '',
    }
    setMissingFields(missingFieldsObj)

    if (Object.values(missingFieldsObj).some(value => value)) {
      return
    }

    setLoading(true)
    setCreateError(false)
    axios
      .put(`/quote/${props.quoteId}`, {
        newOpportunity: {
          ...opportunity,
          AccountId: selectedAccount?.id,
          Owner: {
            Email: user.email ?? '',
          },
        },
      })
      .then(res => {
        props.setCrmOpportunity(res.data)
        props.setAccount(selectedAccount)
        props.setContact(selectedContact)
        props.setOpportunity({ id: res.data.Id, label: res.data.Name })
        props.onClose()
        setOpenSnackbar(true)
      })
      .catch(err => {
        setCreateError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>New Opportunity</DialogTitle>
        <DialogContent>
          <Typography>All fields are required.</Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Opportunity Name"
                value={opportunity.Name}
                onChange={e => setOpportunity(prev => ({ ...prev, Name: e.target.value }))}
                error={missingFields.Name}
                helperText={missingFields.Name ? 'Opportunity name is required' : ''}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                size="small"
                label="Close Date"
                value={opportunity.CloseDate ? dayjs(opportunity.CloseDate) : null}
                onChange={e => setOpportunity(prev => ({ ...prev, CloseDate: e ? e.toDate() : null }))}
                error={missingFields.CloseDate}
                helperText={missingFields.CloseDate ? 'Close date is required' : ''}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Stage"
                value={opportunity.StageName}
                onChange={e => setOpportunity(prev => ({ ...prev, StageName: e.target.value }))}
                error={missingFields.StageName}
                helperText={missingFields.StageName ? 'Stage is required' : ''}
              >
                {salesforceStages.map(stage => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Forecast Category"
                value={opportunity.ForecastCategoryName}
                onChange={e => setOpportunity(prev => ({ ...prev, ForecastCategoryName: e.target.value }))}
                error={missingFields.ForecastCategoryName}
                helperText={missingFields.ForecastCategoryName ? 'Forecast category is required' : ''}
              >
                {salesforceForecastCategories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Core Competency"
                slotProps={{ select: { multiple: true } }}
                value={opportunity.Line_Of_Business__c}
                onChange={e => setOpportunity(prev => ({ ...prev, Line_Of_Business__c: e.target.value }))}
                error={missingFields.Line_Of_Business__c}
                helperText={missingFields.Line_Of_Business__c ? 'Core competency is required' : ''}
              >
                {coreCompetencies.map(competency => (
                  <MenuItem key={competency} value={competency}>
                    {competency}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Set Asides"
                slotProps={{ select: { multiple: true } }}
                value={opportunity.Set_Asides__c}
                onChange={e => setOpportunity(prev => ({ ...prev, Set_Asides__c: e.target.value }))}
                error={missingFields.Set_Asides__c}
                helperText={missingFields.Set_Asides__c ? 'Set aside is required' : ''}
              >
                {setAsides.map(aside => (
                  <MenuItem key={aside} value={aside}>
                    {aside}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Account"
                value={selectedAccount}
                label="Account"
                onChange={(e, value) => {
                  setSelectedAccount(value)
                  setSelectedContact(null)
                }}
                error={missingFields.Account}
                errorText={missingFields.Account ? 'Account is required' : ''}
              />
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Contact"
                accountId={selectedAccount?.id}
                value={selectedContact}
                label="Contact"
                onChange={(e, value) => setSelectedContact(value)}
                error={missingFields.Contact}
                errorText={missingFields.Contact ? 'Contact is required' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateOpportunity} loading={loading}>
            Create New Opportunity
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Opportunity created successfully"
      />
      <Snackbar open={createError}>
        <Alert severity="error" variant="filled" onClose={() => setCreateError(false)}>
          Failed to create opportunity. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function GenerateQuoteDialog(props: {
  open: boolean
  onClose: () => void
  quoteId: string
  quoteName: string
  quote: quoteType
  lineItems: (quoteLineType & { children?: quoteLineType[] })[]
  crmOpportunity: salesforceOpportunityType | null
}) {
  const [error, setError] = useState(false)
  const [template, setTemplate] = useState('')
  const [fileType, setFileType] = useState('')
  const [missingFields, setMissingFields] = useState(false)
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false)

  useEffect(() => {
    if (!props.open) setPdfPreviewOpen(false)
  }, [props.open])

  const handleGenerate = () => {
    if (!template || !fileType) {
      setMissingFields(true)
      return
    }

    if (fileType === 'pdf') {
      if (template !== 'GSA/2GIT') {
        setError(true)
        return
      }
      setPdfPreviewOpen(true)
      return
    }

    axios
      .post(`/quote/${props.quoteId}/print`, { template, fileType }, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `${props.quoteName}.${fileType}`
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        props.onClose()
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
  }

  const closePdfPreview = () => setPdfPreviewOpen(false)
  const account = props.crmOpportunity?.Account ?? null
  const contact = props.crmOpportunity?.Contact ?? null
  const pdfProps = {
    quote: props.quote,
    lineItems: props.lineItems,
    soldToDetails: soldToFromCrm(account, contact),
    shippingDetails: shipToFromCrm(account, contact),
    isrDetails: isrFromCrm(props.crmOpportunity),
    aeDetails: aeFromCrm(props.crmOpportunity),
  }

  return (
    <>
      <Dialog
        open={props.open && !pdfPreviewOpen}
        onClose={() => props.onClose()}
        fullWidth
        className="generate-quote-dialog"
        maxWidth="md"
      >
        <DialogTitle>Generate Quote</DialogTitle>
        <DialogContent>
          <Typography>All fields are required.</Typography>
          <Typography variant="h6">Template</Typography>
          <RadioGroup value={template} onChange={e => setTemplate(e.target.value)}>
            <FormControlLabel value="GSA/2GIT" control={<Radio />} label="GSA/2GIT" />
            <FormControlLabel value="SEWP-V B (VIT)" control={<Radio />} label="SEWP-V B (VIT)" />
            <FormControlLabel value="SEWP-V C (IMPRES)" control={<Radio />} label="SEWP-V C (IMPRES)" />
            <FormControlLabel value="DLA" control={<Radio />} label="DLA" />
            <FormControlLabel value="Open Market" control={<Radio />} label="Open Market" />
            <FormControlLabel value="Open Market - Commercial" control={<Radio />} label="Open Market - Commercial" />
            <FormControlLabel value="ITES-4H" control={<Radio />} label="ITES-4H" />
            <FormControlLabel value="MDA SHIELD" control={<Radio />} label="MDA SHIELD" />
            <FormControlLabel value="SeaPort NxG" control={<Radio />} label="SeaPort NxG" />
          </RadioGroup>
          <Typography variant="h6">File Type</Typography>
          <RadioGroup value={fileType} onChange={e => setFileType(e.target.value)}>
            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            <FormControlLabel value="xlsx" control={<Radio />} label="Excel" />
          </RadioGroup>
          <DialogActions>
            <Button onClick={props.onClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleGenerate} variant="contained">
              Generate Quote
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Dialog
        open={pdfPreviewOpen}
        onClose={closePdfPreview}
        fullWidth
        maxWidth={false}
        className="generate-quote-pdf-preview-dialog"
        slotProps={{
          paper: {
            className: 'generate-quote-pdf-preview-paper',
          },
        }}
      >
        <DialogTitle>PDF Preview</DialogTitle>
        <DialogContent className="generate-quote-pdf-preview-content">
          {pdfPreviewOpen && <QuotePdfPreview className="generate-quote-pdf-preview-frame" {...pdfProps} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePdfPreview} variant="outlined">
            Close
          </Button>
          <Button onClick={() => downloadQuotePdf(pdfProps).catch(err => console.error(err))} variant="contained">
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={missingFields}>
        <Alert severity="info" variant="filled" onClose={() => setMissingFields(false)}>
          Please select a template and file type.
        </Alert>
      </Snackbar>
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to generate quote. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function NTECalculationDialog(props: { open: boolean; onClose: () => void; quoteId: string }) {
  const [nteAmount, setNTEAmount] = useState<number | null>(null)
  const lines = useQuoteLines()
  const [loading, setLoading] = useState(false)

  const handleCalculateNTE = () => {
    if (!nteAmount) {
      return
    }
    setLoading(true)
    const newLineItems = applyNTECalculation(lines.lineItems, nteAmount)
    lines
      .handleBulkEdit(newLineItems)
      .then(() => {
        props.onClose()
        setNTEAmount(null)
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth>
      <DialogTitle>NTE Calculation</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Typography>Enter the final total price to the customer.</Typography>
          <Typography>
            Pressing confirm will apply a consistent margin across every line item in the quote so that it totals the
            Final Total Price to Customer as close as possible. A consistent margin will be applied to every line item
            to two decimal points.
          </Typography>
          <CurrencyInput
            fullWidth
            size="small"
            label="NTE Amount"
            value={nteAmount}
            onChange={e => setNTEAmount(e ?? 0)}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} variant="outlined">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCalculateNTE} loading={loading}>
          Calculate NTE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
